import type { StateCreator } from "zustand";
import type { DBOpsState } from "../useStudioAPI/hooks/dbOps.store";
import type { MegaJSON } from "@certa/studio-core";
import type { DataObjectType } from "@certa/types/src/dataObjects.types";
import { mutateDataObjects } from "@certa/studio-core/src/state/stateMutators";

export type DataObjectsSlice = {
  // Root getters
  getDataObjects: () => Record<string, DataObjectType>;
  getDataObject: (objectTag: string) => DataObjectType | undefined;

  // Object setters
  setDataObject: (objectTag: string, object: DataObjectType) => void;

  // Removers
  removeDataObject: (objectTag: string) => void;

  replaceDataObjects: (dataObjects: Record<string, DataObjectType>) => void;
};

const getStudioObjectFromMegaJSON = (
  megaJSON: MegaJSON,
  tag: string
): DataObjectType | undefined => {
  const object: DataObjectType | undefined = megaJSON.records?.[tag];
  return object ? structuredClone(object) : undefined;
};

export const dataObjectsSlice: StateCreator<
  DBOpsState,
  [],
  [],
  DataObjectsSlice
> = (set, get) => ({
  // Root getters
  getDataObjects: () => {
    const { megaJSON } = get();
    return structuredClone(megaJSON.records || {});
  },
  getDataObject: objectTag => {
    const { megaJSON } = get();
    return getStudioObjectFromMegaJSON(megaJSON, objectTag);
  },

  // Object setters
  setDataObject: (dataObjectTag, dataObject) => {
    const { megaJSON, applyChanges } = get();
    const changes = [
      mutateDataObjects.updateDataObject({
        state: megaJSON,
        dataObjectTag,
        dataObject
      })
    ];
    applyChanges(changes);
  },

  // Removers
  removeDataObject: dataObjectTag => {
    const { megaJSON, applyChanges } = get();
    const changes = [
      mutateDataObjects.removeDataObject({
        state: megaJSON,
        dataObjectTag
      })
    ];
    applyChanges(changes);
  },
  replaceDataObjects: (dataObjects: Record<string, DataObjectType>) => {
    const { megaJSON, applyChanges } = get();
    const changes = [
      mutateDataObjects.replaceDataObjects({
        state: megaJSON,
        dataObjects
      })
    ];
    applyChanges(changes);
  }
});
